import { required, requiredIf } from 'vuelidate/lib/validators';
import { dangerous, zipCode, requireNonWhitespace, symbolsNotAllowed} from '@/forms/Validations/CommonRules';
import { dangerousMessage } from '@/forms/Validations/CommonMessages';

import BaseValidation from '@/forms/Validations/BaseValidation';
import ValidationRule from '@/forms/Validations/ValidationRule';

import Address, { names, AddressFieldType } from '@/common/interfaces/dto/Address';
export { names } from '@/common/interfaces/dto/Address';

import { isShow, getInitCountryValue } from './AddressRow.helper';

const getRequireIf = (field: AddressFieldType, invert: boolean = false) => {
  return requiredIf(((model: any) => {
    const result = isShow(field, model[names.CountryId]);
    return invert ? !result : result;
  }) as any);
};

const getPostalRequired = (value: string, model: Address) => {
  return !isShow(names.PostalCode, model.CountryId) ? true : requireNonWhitespace(value);
};

const getRequireState = () => {
  return requiredIf(((model: any) => isShow(names.StateCode, model[names.CountryId])) as any);
};

const getZipIf = (value: string, model: Address) => {
  return isShow(names.PostalCode, model.CountryId) ? true : zipCode(value);
};

interface AddressParams {
  stripRequired?: boolean;
}

export default (path: string = '', params?: AddressParams) => {
  const rules: ValidationRule[] = [
    {
      name: names.CountryId,
      rules: { required },
      messages: {
        required: 'Select a country',
      },
      modelValue: getInitCountryValue(),
    },
    {
      name: names.Street1,
      rules: { required: requireNonWhitespace, dangerous },
      messages: {
        required: 'Enter an address',
        dangerous: dangerousMessage,
      },
    },
    {
      name: names.Street2,
      rules: { dangerous },
      messages: {
        dangerous: dangerousMessage,
      },
    },
    {
      name: names.City,
      rules: { required: requireNonWhitespace, dangerous, symbolsNotAllowed},
      messages: {
        required: 'Enter a city',
        dangerous: dangerousMessage,
        symbolsNotAllowed: `The symbols #^*?' are not allowed in city names`
      },
    },
    {
      name: names.StateCode,
      rules: {
        requiredState: getRequireState(),
      },
      messages: {
        requiredState: 'Select a state',
      },
    },
    {
      name: names.PostalCode,
      rules: {
        requiredPostal: getPostalRequired,
        required: getRequireIf(names.PostalCode, true),
        zipCode: getZipIf,
        dangerous,
      },
      messages: {
        requiredPostal: 'Enter a postal code',
        required: 'Enter a zip code',
        zipCode: 'Enter a valid zip code',
        dangerous: dangerousMessage,
      },
    },
  ];

  if (params?.stripRequired) {
    rules.forEach((r) => {
      const { rules, messages }: { rules: any; messages: any } = r;

      delete rules.required;
      delete rules.requiredAus;
      delete rules.requiredPostal;
      delete rules.requiredState;

      delete messages.required;
      delete messages.requiredAus;
      delete messages.requiredPostal;
      delete messages.requiredState;
    });
  }

  return new BaseValidation(rules, path);
};
